import React from "react";
import { LogIn, LogOut, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useAuth } from "../lib/auth";

export default function AuthButton() {
  const { user, loading, login, logout, openPaywall } = useAuth();

  if (loading) {
    return (
      <div
        data-testid="auth-loading"
        className="h-10 w-24 rounded-xl bg-gray-100 animate-pulse"
      />
    );
  }

  if (!user) {
    return (
      <Button
        data-testid="google-signin-button"
        onClick={login}
        className="rounded-xl bg-gray-900 hover:bg-gray-700 text-white h-10 px-4 font-medium"
      >
        <LogIn className="w-4 h-4 mr-2" />
        Sign in
      </Button>
    );
  }

  const initial = (user.name || user.email || "?").charAt(0).toUpperCase();

  return (
    <div className="flex items-center gap-3">
      {!user.lifetime_paid && (
        <Button
          data-testid="unlock-button"
          onClick={openPaywall}
          className="rounded-xl bg-[#673A80] hover:bg-[#552d6b] text-white h-10 px-4 font-medium"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Unlock
        </Button>
      )}
      {user.lifetime_paid && (
        <span
          data-testid="paid-badge"
          className="hidden md:inline-flex items-center rounded-full bg-[#673A80] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1"
        >
          Lifetime
        </span>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="user-menu-trigger"
            className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center overflow-hidden border-2 border-gray-900"
          >
            {user.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="font-bold">{initial}</span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-xl w-56">
          <DropdownMenuLabel>
            <div className="flex flex-col">
              <span className="font-semibold truncate">{user.name}</span>
              <span className="text-xs text-gray-500 truncate">{user.email}</span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="logout-menu-item"
            onClick={logout}
            className="cursor-pointer"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
